/**
 * tpWorkbookParserABS.ts
 *
 * Client-side parser for ABS TP request workbooks (.xlsx).
 *
 * ABS workbook typically has:
 *   - A main sheet with one row per ABS section (4.1–4.17)
 *   - Possibly a separate sheet for 4.7 (remote access / untrusted networks)
 *   - Column headers differ from DNV, detection is keyword-based
 *
 * Section key mapping (ABS clause → internal key):
 *   4.1  vessel_asset_inventory
 *   4.2  security_zones_and_network_segmentation
 *   4.3  network_protection_safeguards
 *   4.4  malicious_code_protection
 *   4.5  access_control
 *   4.6  wireless_communication
 *   4.7  remote_access_and_untrusted_networks
 *   4.8  mobile_and_portable_devices
 *   4.9  network_operation_monitoring
 *   4.10 verification_and_diagnostic_functions
 *   4.11 incident_response_plan          (static note, parser outputs default)
 *   4.12 local_independent_manual_operation
 *   4.13 network_isolation
 *   4.14 fallback_to_minimal_risk_condition
 *   4.15 recovery_plan
 *   4.16 backup_and_restore_capability
 *   4.17 controlled_shutdown_reset_rollback_restart
 *
 * ABS special rules:
 *   4.1  appendix-driven, only test_results field in template
 *   4.2  partial, only detailed_steps + expected_result
 *   4.11 static note, rendered as {{incident_response_plan_note}}
 */

import * as XLSX from 'xlsx'
import type { TPWorkbookResult, TPSectionContent } from './tpWorkbookParser'

// ── Section key map: identifier fragment → section key ───────────────────────
const ABS_CLAUSE_TO_KEY: Record<string, string> = {
  '4.1':  'vessel_asset_inventory',
  '4.2':  'security_zones_and_network_segmentation',
  '4.3':  'network_protection_safeguards',
  '4.4':  'malicious_code_protection',
  '4.5':  'access_control',
  '4.6':  'wireless_communication',
  '4.7':  'remote_access_and_untrusted_networks',
  '4.8':  'mobile_and_portable_devices',
  '4.9':  'network_operation_monitoring',
  '4.10': 'verification_and_diagnostic_functions',
  '4.11': 'incident_response_plan',
  '4.12': 'local_independent_manual_operation',
  '4.13': 'network_isolation',
  '4.14': 'fallback_to_minimal_risk_condition',
  '4.15': 'recovery_plan',
  '4.16': 'backup_and_restore_capability',
  '4.17': 'controlled_shutdown_reset_rollback_restart',
}

const ABS_TITLE_TO_KEY: Array<[string, string]> = [
  ['vessel asset inventory',             'vessel_asset_inventory'],
  ['security zone',                      'security_zones_and_network_segmentation'],
  ['network protection',                 'network_protection_safeguards'],
  ['malicious code', 'malicious_code_protection'],
  ['antivirus',                          'malicious_code_protection'],
  ['access control',                     'access_control'],
  ['wireless communication',             'wireless_communication'],
  ['wireless network',                   'wireless_communication'],
  ['remote access',                      'remote_access_and_untrusted_networks'],
  ['untrusted network',                  'remote_access_and_untrusted_networks'],
  ['mobile and portable',                'mobile_and_portable_devices'],
  ['network operation monitoring',       'network_operation_monitoring'],
  ['verification and diagnostic',        'verification_and_diagnostic_functions'],
  ['incident response',                  'incident_response_plan'],
  ['local, independent',                 'local_independent_manual_operation'],
  ['local independent',                  'local_independent_manual_operation'],
  ['network isolation',                  'network_isolation'],
  ['fallback',                           'fallback_to_minimal_risk_condition'],
  ['minimal risk',                       'fallback_to_minimal_risk_condition'],
  ['recovery plan',                      'recovery_plan'],
  ['backup and restore',                 'backup_and_restore_capability'],
  ['controlled shutdown',                'controlled_shutdown_reset_rollback_restart'],
]

/** default static note for 4.11 when no workbook row provides one */
const INCIDENT_RESPONSE_NOTE =
  'Demonstration not required in Commissioning Phase.'

function str(val: unknown): string {
  if (val === null || val === undefined) return ''
  return String(val).trim()
}

function findCol(headers: string[], ...keywords: string[]): number {
  const lc = headers.map(h => h.toLowerCase())
  for (const kw of keywords) {
    const idx = lc.findIndex(h => h.includes(kw.toLowerCase()))
    if (idx !== -1) return idx
  }
  return -1
}

function resolveKey(identifier: string): string | null {
  const id = identifier.trim()
  // match by clause number
  for (const [clause, key] of Object.entries(ABS_CLAUSE_TO_KEY)) {
    if (id.startsWith(clause)) return key
  }
  // match by title keyword
  const lower = id.toLowerCase()
  for (const [kw, key] of ABS_TITLE_TO_KEY) {
    if (lower.includes(kw)) return key
  }
  return null
}

function empty(): TPSectionContent {
  return { test_setup: '', initial_condition: '', test_equipment: '', detailed_steps: '', expected_result: '' }
}

export async function parseTPWorkbookABS(file: File): Promise<TPWorkbookResult> {
  const buffer = await file.arrayBuffer()
  const wb = XLSX.read(buffer, { type: 'array' })

  const warnings: string[]    = []
  const unsupported: string[] = []
  const parsedSheets: string[] = []
  const sections: Partial<Record<string, TPSectionContent>> = {}

  // try common ABS sheet names
  const mainSheetName = wb.SheetNames.find(n =>
    /test\s*procedure|tp\s*info|abs\s*tp|onboard\s*test/i.test(n)
  ) ?? wb.SheetNames[0]

  if (!mainSheetName) {
    warnings.push('No sheets found in workbook')
    return { sections: {}, warnings, unsupported, parsedSheets, comm411RowCount: 0 }
  }

  const sheet = wb.Sheets[mainSheetName]
  const rows: unknown[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' })
  if (rows.length < 2) {
    warnings.push(`Sheet "${mainSheetName}" has fewer than 2 rows`)
    return { sections: {}, warnings, unsupported, parsedSheets, comm411RowCount: 0 }
  }
  parsedSheets.push(mainSheetName)

  const headers = (rows[0] as string[]).map(str)
  let idCol = findCol(headers, 'no.', 'section', 'clause', '4.')
  if (idCol === -1) idCol = 0

  const setupCol     = findCol(headers, 'necessary test setup', 'test setup', 'setup', 'initial condition')
  const conditionCol = findCol(headers, 'initial condition', 'condition')
  const equipCol     = findCol(headers, 'test equipment', 'equipment')
  const stepsCol     = findCol(headers, 'detailed step', 'step', 'procedure')
  const resultCol    = findCol(headers, 'expected result', 'result', 'acceptance')
  const testResCol   = findCol(headers, 'test result', 'finding', 'remark')

  const getCell = (row: unknown[], col: number, fallbackOffset: number): string =>
    col !== -1 ? str(row[col]) : str((row as unknown[])[idCol + fallbackOffset] ?? '')

  for (let r = 1; r < rows.length; r++) {
    const row = rows[r] as unknown[]
    const identifier = str(row[idCol])
    if (!identifier) continue

    const key = resolveKey(identifier)
    if (!key) {
      warnings.push(`ABS workbook row ${r + 1}: unrecognised section "${identifier}" — skipped`)
      continue
    }

    if (key === 'incident_response_plan') {
      // 4.11: static note, use workbook value if present, else default
      const noteValue = getCell(row, stepsCol, 4) || getCell(row, resultCol, 5) || INCIDENT_RESPONSE_NOTE
      sections[key] = { ...empty(), detailed_steps: noteValue, test_setup: INCIDENT_RESPONSE_NOTE }
      // also expose as 'note' field for the {{incident_response_plan_note}} placeholder
      ;(sections[key] as any).note = INCIDENT_RESPONSE_NOTE
      continue
    }

    if (key === 'vessel_asset_inventory') {
      // 4.1: appendix-driven, only test_results
      const testResults = testResCol !== -1 ? str(row[testResCol]) : ''
      sections[key] = { ...empty() }
      ;(sections[key] as any).test_results = testResults
      continue
    }

    if (key === 'security_zones_and_network_segmentation') {
      // 4.2: only detailed_steps + expected_result
      sections[key] = {
        ...empty(),
        detailed_steps:  getCell(row, stepsCol,  4),
        expected_result: getCell(row, resultCol, 5),
      }
      continue
    }

    sections[key] = {
      test_setup:        getCell(row, setupCol,     1),
      initial_condition: getCell(row, conditionCol, 2),
      test_equipment:    getCell(row, equipCol,     3),
      detailed_steps:    getCell(row, stepsCol,     4),
      expected_result:   getCell(row, resultCol,    5),
    }
  }

  // ensure 4.11 always has a note even if not in workbook
  if (!sections['incident_response_plan']) {
    sections['incident_response_plan'] = { ...empty() }
    ;(sections['incident_response_plan'] as any).note = INCIDENT_RESPONSE_NOTE
  }

  return {
    sections: sections as any,
    warnings,
    unsupported,
    parsedSheets,
    comm411RowCount: 0,  // ABS doesn't use the DNV-style 4.11 SR aggregation
  }
}
